/**
 * Avatar Combinations Generator
 * Creates all possible option variations for grid display
 */

import { Avatar, type Style } from '@dicebear/core';
import {
  toDicebearOptions,
  type StyleOptions,
} from './avatarOptionDescriptor';

export interface AvatarCombination {
  active: boolean;
  isCustomColor?: boolean;
  options: Record<string, string>;
  avatar: {
    toString: () => string;
    toDataUri: () => string;
  };
}

export type AvatarCombinations = Record<string, AvatarCombination[]>;

/**
 * True when every choice in a group renders the same avatar, which happens
 * whenever an option cannot show given the rest of the selection: a hat colour
 * with no hat, a shirt graphic on a plain shirt, earrings hidden under hair, or
 * a background a style paints over. Such a group is a grid of identical
 * thumbnails, so it is dropped until it means something again.
 */
function isInert(items: AvatarCombination[]): boolean {
  if (items.length < 2) return true;

  const first = items[0]!.avatar.toString();
  return items.every((item) => item.avatar.toString() === first);
}

/**
 * Generate avatar combinations for all options
 */
export function generateAvatarCombinations(
  style: Style,
  styleOptions: StyleOptions,
  selectedOptions: Record<string, string>,
  seed: string,
): AvatarCombinations {
  const combinations: AvatarCombinations = {};

  for (const key in styleOptions) {
    if (!Object.prototype.hasOwnProperty.call(styleOptions, key)) {
      continue;
    }

    combinations[key] = [];

    const styleOption = styleOptions[key];
    if (!styleOption) continue;

    const currentValue = selectedOptions[key];

    let activeItem: AvatarCombination | null = null;

    for (const value of styleOption.values) {
      const options = {
        ...selectedOptions,
        [key]: value,
      };

      const avatar = new Avatar(style, {
        seed,
        ...toDicebearOptions(options, styleOptions),
      });
      const active = currentValue === value;

      const item: AvatarCombination = {
        active,
        avatar,
        options,
      };

      if (item.active) {
        activeItem = item;
      }

      combinations[key].push(item);
    }

    // Add custom color option for color fields
    if (styleOption.isColor) {
      const options = {
        ...selectedOptions,
        [key]: currentValue || '',
      };

      combinations[key].unshift({
        active: !activeItem,
        isCustomColor: true,
        avatar: new Avatar(style, {
          seed,
          ...toDicebearOptions(options, styleOptions),
        }),
        options,
      });
    }

    if (isInert(combinations[key])) {
      delete combinations[key];
    }
  }

  return combinations;
}
